import { useState } from "react";
import styled from "styled-components";

import { AlertBanner } from "@runwayhealth/runway-components-react";
import { ConditionType } from "@types-runway/condition";
import { NoticeMessage } from "@types-runway/questionnaire";

import Description from "../description";

const Section = styled.div`
  padding: 0 20px;
`;

const SectionTitle = styled.h3`
  font-family: inherit;
  font-weight: 700;
  font-size: 1.1rem;
  margin: 0 0 12px 0;
  color: #333;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 16px 20px;
`;

const CollapseToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  color: #333;

  &:hover {
    color: #4272da;
  }
`;

const Arrow = styled.span<{ $isOpen: boolean }>`
  display: inline-block;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(90deg)" : "rotate(0deg)")};
`;

const CollapsibleContent = styled.div<{ $isOpen: boolean }>`
  overflow: hidden;
  max-height: ${({ $isOpen }) => ($isOpen ? "2000px" : "0")};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: max-height 0.3s ease, opacity 0.2s ease;
`;

type ConditionInfoContentProps = {
  conditionType: ConditionType;
  conditionName: string;
  pricingWarning: NoticeMessage | null;
  selectedMedicationName?: string;
};

const ConditionInfoContent = ({
  conditionType,
  conditionName,
  pricingWarning,
  selectedMedicationName,
}: ConditionInfoContentProps) => {
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(!pricingWarning);

  if (!pricingWarning) {
    return <Description conditionType={conditionType} selectedMedicationName={selectedMedicationName} />;
  }

  return (
    <>
      <Section>
        <SectionTitle>Pricing</SectionTitle>
        <AlertBanner
          variant={pricingWarning.variant}
          isRounded
          data-testid="condition-pricing-warning"
        >
          <AlertBanner.Message>{pricingWarning.message}</AlertBanner.Message>
        </AlertBanner>
      </Section>
      <Divider />
      <CollapseToggle
        onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
        data-testid="condition-info-toggle"
      >
        <Arrow $isOpen={isDescriptionOpen}>&#9654;</Arrow>
        About {conditionName}
      </CollapseToggle>
      <CollapsibleContent $isOpen={isDescriptionOpen}>
        <Description conditionType={conditionType} selectedMedicationName={selectedMedicationName} />
      </CollapsibleContent>
    </>
  );
};

export default ConditionInfoContent;
